import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from './useAuth';
import {
	setApiAuthUiHandlers,
	setApiLiveTokenGetter,
	setApiSessionHydrator,
} from '../api/axios';
import { useUI } from '../providers/MoleUIContext';
import { useLocation } from 'react-router-dom';

/**
 * Wires React session + UI into the module-level axios interceptors.
 * Auth headers themselves are always applied (see api/axios.ts) so requests
 * never race React effect mount order.
 */
export const useAxiosInterceptor = () => {
	const { session, sessionRef, setSession } = useAuth();
	const { setLoginModalOpen, setLoadingMessage, t } = useUI();
	const location = useLocation();

	// Keep live token getter in sync every render (no effect delay).
	useEffect(() => {
		setApiLiveTokenGetter(
			() =>
				sessionRef.current?.access_token ||
				session?.access_token ||
				null
		);
		setApiSessionHydrator((next) => {
			setSession(next);
		});
		return () => {
			setApiLiveTokenGetter(null);
			setApiSessionHydrator(null);
		};
	}, [session, sessionRef, setSession]);

	useEffect(() => {
		let sessionToastId: string | number = -1;
		let networkToastId: string | number = -1;
		let timeoutToastId: string | number = -1;

		setApiAuthUiHandlers({
			onSessionExpired: () => {
				// Full-page login already is the re-auth UI — do not stack LoginModal.
				if (location.pathname === '/login') {
					return;
				}
				if (!toast.isActive(sessionToastId)) {
					sessionToastId = toast.warning(t('common:session.expired'));
				}
				setLoginModalOpen(true);
			},
			onSuspended: (message) => {
				toast.error(message);
			},
			onClientError: (message) => {
				toast.error(message);
			},
			onNetworkError: () => {
				if (!toast.isActive(networkToastId)) {
					networkToastId = toast.error(t('common:toast.error.network'), {
						autoClose: 1000,
					});
				}
			},
			onTimeout: () => {
				if (!toast.isActive(timeoutToastId)) {
					timeoutToastId = toast.error(
						t('common:toast.error.request_timeout'),
						{ autoClose: 1000 }
					);
				}
			},
			onServerError: (message) => {
				toast.error(message || t('common:toast.error.server'));
			},
			onRequestFailed: () => {
				setLoadingMessage(null);
			},
			t,
		});

		return () => {
			setApiAuthUiHandlers({});
		};
	}, [setLoginModalOpen, setLoadingMessage, t, location.pathname]);
};
